import { Box, Dialog, IconButton, Typography, useTheme } from "@mui/material";
import useFetch from "../../../api/useFetch";
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from "../../../components/exo/ExoTableToolbar";
import { useTranslation } from "react-i18next";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestorePageIcon from "@mui/icons-material/RestorePage";
import { GenerateTimeSheets } from "./GenerateTimeSheets";
import { useMemo, useState } from "react";
import { renderHours } from "../../time-tracking/worktime";
import ExoAccordion from "../../../components/exo/ExoAccordion";
import ExoAvatar from "../../../components/exo/ExoAvatar";
import { ExoFilePreview } from "../../../components/exo/ExoFilePreview";
import moment from "moment";
import { API_DELETE, API_POST, DBRequest, useConfig } from "../../../api/api";
import { tokens } from "../../../global/theme/tokens";
import Cookies from "universal-cookie";

const cookies = new Cookies();

const TimesheetsTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const { apiData, setApiData, isLoading, reloadApiData } = useFetch(
    "time-sheets?groupBy=endDate&sortBy=startDate&perPage=12"
  );

  const [open, setOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  function handleNewElement() {
    setOpen(false);
    reloadApiData();
  }

  function handleDelete(monthKey, deletedRow) {
    setApiData((prev) => ({
      ...prev,
      data: prev.data
        .map((group) => {
          if (group.month !== monthKey) return group;
          return {
            ...group,
            items: group.items.filter((item) => item.id !== deletedRow.id),
          };
        })
        .filter((group) => group.items.length > 0),
    }));
  }

  function handleRegenerate(monthKey, newItem, oldItem) {
    setApiData((prev) => ({
      ...prev,
      data: prev.data.map((group) => {
        if (group.month !== monthKey) return group;
        return {
          ...group,
          items: group.items.map((item) =>
            item.id === oldItem.id ? newItem.data : item
          ),
        };
      }),
    }));
  }

  function handleDownloadMonth(e, monthKey) {
    e.stopPropagation();
    if (!config) return;

    const [year, month] = monthKey.split("-");
    const url = `${config.API_URL}time-sheets/download/${year}/${month}`;

    fetch(url, {
      headers: {
        Authorization: "Bearer " + cookies.get("accessToken"),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Download failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `Timesheets_${monthKey}.zip`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => console.error("Download error:", err));
  }

  function getMonthTotal(items) {
    return items.reduce((sum, item) => sum + (item.minutesTracked || 0), 0);
  }

  function sortItemsAlphabetically(items) {
    return [...items].sort((a, b) => {
      const nameA = `${a.user?.lastName} ${a.user?.firstName}`.toLowerCase();
      const nameB = `${b.user?.lastName} ${b.user?.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  const accordionElements = useMemo(
    () =>
      apiData?.data?.map((group) => ({
        customSummary: (
          <Box className="flex flex-row justify-between items-center w-full pr-2">
            <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
              {moment(group.month, "YYYY-MM").format("MMMM YYYY")}
            </Typography>
            <Box className="flex flex-row items-center gap-3">
              <Typography
                variant="body2"
                className="monospace"
                sx={{ opacity: 0.7 }}
              >
                {renderHours(getMonthTotal(group.items))} h
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => handleDownloadMonth(e, group.month)}
                title={t("Download all")}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ),
        content: (
          <Box className="flex flex-col">
            {sortItemsAlphabetically(group.items).map((item) => (
              <EmployeeRow
                key={item.id}
                item={item}
                colors={colors}
                config={config}
                onPreview={() => setPreviewFile(item.file)}
                onDelete={() => handleDelete(group.month, item)}
                onRegenerate={(newItem) =>
                  handleRegenerate(group.month, newItem, item)
                }
                t={t}
              />
            ))}
          </Box>
        ),
      })) || [],
    [apiData, colors, config]
  );

  return (
    <Box className="w-full flex flex-col gap-0 h-full">
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t("Generate")}
          icon={<AutoFixNormalIcon />}
          onClick={() => setOpen(true)}
        />
      </ExoTableToolbar>
      {isLoading ? (
        <Box className="flex justify-center items-center py-8">
          <Typography sx={{ opacity: 0.5 }}>{t("Loading")}...</Typography>
        </Box>
      ) : accordionElements.length === 0 ? (
        <Box className="flex justify-center items-center py-8">
          <Typography sx={{ opacity: 0.5 }}>
            {t("No data available")}
          </Typography>
        </Box>
      ) : (
        <ExoAccordion elements={accordionElements} />
      )}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <GenerateTimeSheets
          onResponse={handleNewElement}
          onClose={() => setOpen(false)}
        />
      </Dialog>
      <ExoFilePreview
        open={Boolean(previewFile)}
        src={previewFile?.temporaryUrl}
        mimeType={previewFile?.mimeType || "application/pdf"}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

const EmployeeRow = ({
  item,
  colors,
  config,
  onPreview,
  onDelete,
  onRegenerate,
  t,
}) => {
  const user = item.user;

  function handleDelete() {
    DBRequest({
      config,
      path: `time-sheets/${item.id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(),
    });
  }

  function handleRegenerate() {
    DBRequest({
      config,
      path: `time-sheets/${item.id}/regenerate`,
      method: API_POST({}),
      onResponse: (res) => onRegenerate(res),
    });
  }

  return (
    <Box
      className="flex flex-row items-center justify-between px-3 py-2 rounded-lg transition-colors"
      sx={{
        "&:hover": {
          backgroundColor: colors.bgInput,
        },
      }}
    >
      <Box className="flex flex-row items-center gap-3">
        <ExoAvatar
          picture={user?.picture?.temporaryUrl || ""}
          name={`${user?.firstName || ""} ${user?.lastName || ""}`}
          size="36px"
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {user?.lastName} {user?.firstName}
        </Typography>
      </Box>
      <Box className="flex flex-row items-center gap-2">
        <Typography
          variant="body2"
          className="monospace"
          sx={{ opacity: 0.7 }}
        >
          {renderHours(item.minutesTracked)} h
        </Typography>
        <IconButton size="small" onClick={onPreview} title={t("Preview")}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleRegenerate}
          title={t("Regenerate")}
        >
          <RestorePageIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleDelete} title={t("Delete")}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TimesheetsTab;
